import { useEffect, useState } from 'react'
import axios from '@fs/zion-axios'
import { sgBaseUrl } from '@fs/zion-config'
import zionDebug from '@fs/zion-debug'

// turn on in browser console with: localStorage.debug = 'fs:search:auth:*'
const debug = zionDebug('search:auth:useImagePermission')

// Extract the image ark (e.g. "3:1:3Q9M-CSWN-VSHW-N") from a film-viewer path
// like /ark:/61903/3:1:3Q9M-CSWN-VSHW-N
export function getImageArkFromPath(path) {
  return path?.match(/61903\/(3:\d:[^/?]+)/)?.[1]
}

// Only ThemisPrmNotSignedIn means viewable without logging in. ThemisPrmAnyone is also legally
// public, but FamilySearch deliberately login-walls it to deter harvesters (per rights-management)
// — do NOT treat it as public here; that would remove an intentional login wall. See the README.
const isPubliclyViewable = (themisPermission) =>
  typeof themisPermission === 'string' && themisPermission.includes('ThemisPrmNotSignedIn')

/**
 * Ask DAS whether the image in `pathname` is publicly viewable, instead of the deprecated GedcomX
 * collection lookup. Fetching data is a legitimate effect (external system), so it lives here in a
 * dedicated hook with cleanup rather than inline in the route component.
 *
 * Paths without an image ark resolve to non-public immediately (no request).
 *
 * @param {string} pathname - the current route pathname
 * @returns {{ loading: boolean, isPublic: boolean, error: unknown }}
 */
export default function useImagePermission(pathname) {
  const imageArk = getImageArkFromPath(pathname)
  // Cache each ark's resolved permission for the life of this mount. The film viewer navigates
  // between arks client-side (history.replace) without remounting this hook, so `loading` is
  // DERIVED during render (no cached answer for THIS ark yet) rather than stored — the previous
  // image's answer never leaks, and returning to an already-checked image doesn't flash loading
  // again. See "You Might Not Need an Effect".
  const [resolvedByArk, setResolvedByArk] = useState({})
  const resolved = imageArk ? resolvedByArk[imageArk] : undefined

  useEffect(() => {
    if (!imageArk || resolved !== undefined) return undefined

    const controller = new AbortController()
    // own instance keeps auth/lang headers but skips the auto-login interceptor, so we can read
    // the real permission rather than being redirected
    axios
      .create()
      .get(`${sgBaseUrl}/service/records/storage/dascloud/das/v2/${imageArk}/permission`, {
        signal: controller.signal,
      })
      .then((response) => {
        debug(`image permission for ${imageArk}:`, response.data)
        setResolvedByArk((prev) => ({ ...prev, [imageArk]: { isPublic: isPubliclyViewable(response.data) } }))
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          debug(`image permission error for ${imageArk}:`, error?.response?.status || error)
          // fail closed (not public), but surface the error so consumers can tell "not public"
          // apart from "request failed"
          setResolvedByArk((prev) => ({
            ...prev,
            [imageArk]: { isPublic: false, error: error || new Error('Could not get image permission') },
          }))
        }
      })
    return () => {
      controller.abort()
    }
  }, [imageArk, resolved])

  // A path without an image ark is never public (and needs no request).
  if (!imageArk) return { loading: false, isPublic: false, error: undefined }

  // Until DAS has answered for THIS ark, report loading — never inherit another ark's answer.
  const loading = resolved === undefined
  return { loading, isPublic: loading ? false : resolved.isPublic, error: resolved?.error }
}
